/**
 * RenameFolderModal — Modal for renaming an existing folder.
 *
 * Triggered from the 3-dots menu "Rename" action on a folder.
 * Pre-fills the input with the current folder name and uses the
 * patchFile mutation to update it. Submit is blocked while the
 * trimmed name equals the original name.
 *
 * Owns the patch mutation, local name state, pre-fill effect, and toast
 * copy. Delegates the entire UI to the shared `FolderNameModal`.
 */

import { useState, useCallback, useEffect } from "react";
import FolderNameModal from "../FolderNameModal";
import { usePatchFileMutation } from "../../../../../../../../../../redux/services/kbFilesApi";
import { customToast } from "../../../../../../../../../../utils/custom-toast/custom-toast";
import type { RenameFolderModalProps } from "../../../types/fileManagement.types";
import { RENAME_FOLDER } from "../../../constants/fileManagement.constants";

const RenameFolderModal = ({
  open,
  onOpenChange,
  entity,
}: RenameFolderModalProps) => {
  const [folderName, setFolderName] = useState("");
  const [patchFile, { isLoading }] = usePatchFileMutation();

  const originalName = entity?.name ?? "";
  const trimmedName = folderName.trim();
  const isUnchanged = trimmedName === originalName;
  const isSubmitDisabled = isLoading || trimmedName.length === 0 || isUnchanged;

  useEffect(() => {
    if (open && entity) {
      setFolderName(entity.name);
    }
  }, [open, entity]);

  const handleClose = useCallback(() => {
    setFolderName("");
    onOpenChange(false);
  }, [onOpenChange]);

  const handleSubmit = useCallback(async () => {
    if (!entity || !trimmedName || isUnchanged) return;

    try {
      await patchFile({
        file_id: entity?.file_id,
        name: trimmedName,
      }).unwrap();

      customToast(RENAME_FOLDER.SUCCESS, "success");
      handleClose();
    } catch (error) {
      const errorDetail =
        (error as { data?: { detail?: string } })?.data?.detail ||
        RENAME_FOLDER.ERROR;
      customToast(errorDetail, "error");
    }
  }, [entity, trimmedName, isUnchanged, patchFile, handleClose]);

  return (
    <FolderNameModal
      open={open}
      title={RENAME_FOLDER.TITLE}
      value={folderName}
      onChange={setFolderName}
      onSubmit={handleSubmit}
      onCancel={handleClose}
      isSubmitting={isLoading}
      isSubmitDisabled={isSubmitDisabled}
      submitLabel={isLoading ? RENAME_FOLDER.SAVING : RENAME_FOLDER.SAVE}
      cancelLabel={RENAME_FOLDER.CANCEL}
      labelText={RENAME_FOLDER.LABEL}
      placeholder={RENAME_FOLDER.PLACEHOLDER}
    />
  );
};

export default RenameFolderModal;
